import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { FaBackward, FaEdit, FaSave } from "react-icons/fa";
import { Container } from "reactstrap";
import TextEditor from "../../../../components/TextEditor";
import ReactSelect from "react-select";
import {
  CASINO_BONUS_TYPES,
  CASINO_OWNERS,
  CASINO_TESTING_AGENCIES,
  COUNTRIES,
  CURRENCIES,
  CUSTOMER_SUPPORT,
  DEPOSIT_METHODS,
  LANGUAGES,
  PULLING_METHODS,
} from "../../../../constants/OptionsData";
import { useLocation, useNavigate } from "react-router-dom";
import { CasinoAPI } from "../../../../api/casino";
import SPINNER from "../../../../components/Spinner";

interface SectionWrapperProps {
  children: React.ReactNode;
  header: string;
}
const SectionWrapper = (props: SectionWrapperProps) => {
  const { header, children } = props;
  return (
    <section className="w-full h-fit my-3 flex flex-col items-center">
      <h2 className="border-b pb-4 pl-3 mb-3 font-semibold text-gray-600 w-full">
        {header}
      </h2>
      <div className="w-full flex flex-col">{children}</div>
    </section>
  );
};

export interface OptionsType {
  value: string;
  label: string;
}
interface FormData {
  category: OptionsType;
  contentName: string;
  refURL: string;
  taxLink: string;
  freeBonusText: string;
  welcomeBonusText: string;
  bonusShortDesc: string;
  bonusLongDesc: string;
  description: string;
  picture: string;
  cover: string;
  homeSequence: string;
  categoryRank: string;
  status: string;
  website: string;
  email: string;
  phone: string;
  operatingSince: string;
  owners: OptionsType[];
  testingAgency: OptionsType[];
  currencies: OptionsType[];
  depositMethods: OptionsType[];
  pullingMethods: OptionsType[];
  customerSupport: OptionsType[];
  languages: OptionsType[];
  mobileApp: OptionsType;
  bonusTypes: OptionsType[];
  allowedCountries: OptionsType[];
  seoTitle: string;
  seoKeywords: string;
  seoDesc: string;
}
const initialFormData: FormData = {
  category: { value: "Online Casino", label: "Online Casino" },
  contentName: "",
  refURL: "",
  taxLink: "",
  freeBonusText: "",
  welcomeBonusText: "",
  bonusShortDesc: "",
  bonusLongDesc: "",
  description: "",
  homeSequence: "",
  categoryRank: "",
  picture: "",
  cover: "",
  status: "active",
  website: "",
  email: "",
  phone: "",
  operatingSince: "",
  owners: [],
  testingAgency: [],
  currencies: [],
  depositMethods: [],
  pullingMethods: [],
  customerSupport: [],
  languages: [],
  mobileApp: { value: "No", label: "No" },
  bonusTypes: [],
  allowedCountries: [],
  seoTitle: "",
  seoKeywords: "",
  seoDesc: "",
};

const AddNewCasino = () => {
  document.title = "Casino24 - Add New Casino";

  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [picture, setPicture] = useState<File>();
  const [cover, setCover] = useState<File>();

  useEffect(() => {
    const fetchCasino = async () => {
      setLoading(true);
      if (state && state.id) {
        const res = await CasinoAPI.getCasino(state.id);
        if (res.data) {
          const updatedData = { ...res.data };
          if (updatedData.homeSequence === "1000000") {
            updatedData.homeSequence = "";
          }
          setFormData(updatedData);
        }
      }
      setLoading(false);
    };
    fetchCasino();
  }, [state]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, files } = e.target as HTMLInputElement;

    if (name === "picture" && files && files.length > 0) {
      setPicture(files[0]);
    } else if (name === "cover" && files && files.length > 0) {
      setCover(files[0]);
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: files && files.length > 0 ? files[0] : value,
      }));
    }
  };

  const handleEditorChange = (name: string, value: string) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSelectChange = (selectedOptions: any, actionMeta: any) => {
    setFormData((prevData) => ({
      ...prevData,
      [actionMeta.name]: selectedOptions,
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    let pictureUrl = formData.picture;
    let coverUrl = formData.cover;
    if (picture !== undefined) {
      const res = (await CasinoAPI.uploadImage(picture)) as {
        url?: string;
        success?: boolean;
      };
      if (res.success && res.url) {
        pictureUrl = res.url;
      } else alert("Something went wrong P. Try Again!");
    }
    if (cover !== undefined) {
      const res = (await CasinoAPI.uploadImage(cover)) as {
        url?: string;
        success?: boolean;
      };
      if (res.success && res.url) {
        coverUrl = res.url;
      } else alert("Something went wrong C. Try Again!");
    }
    const dataToSend = {
      category: formData.category,
      contentName: formData.contentName,
      refURL: formData.refURL,
      taxLink: formData.taxLink,
      freeBonusText: formData.freeBonusText,
      welcomeBonusText: formData.welcomeBonusText,
      bonusShortDesc: formData.bonusShortDesc,
      bonusLongDesc: formData.bonusLongDesc,
      description: formData.description,
      picture: pictureUrl,
      cover: coverUrl,
      homeSequence:
        formData.homeSequence === "" ? "1000000" : formData.homeSequence,
      categoryRank: formData.categoryRank,
      status: formData.status,
      website: formData.website,
      email: formData.email,
      phone: formData.phone,
      operatingSince: formData.operatingSince,
      owners: formData.owners,
      testingAgency: formData.testingAgency,
      currencies: formData.currencies,
      depositMethods: formData.depositMethods,
      pullingMethods: formData.pullingMethods,
      customerSupport: formData.customerSupport,
      languages: formData.languages,
      mobileApp: formData.mobileApp,
      bonusTypes: formData.bonusTypes,
      allowedCountries: formData.allowedCountries,
      seoTitle: formData.seoTitle,
      seoKeywords: formData.seoKeywords,
      seoDesc: formData.seoDesc,
    };

    try {
      if (state && state.id) {
        await CasinoAPI.updateCasino(state.id, dataToSend);
        navigate("/vips/casinos-list");
      } else {
        const res = await CasinoAPI.saveCasino(dataToSend);
        if (res.data.id) {
          setFormData(initialFormData);
          navigate("/vips/casinos-list");
        }
      }
    } catch (error) {
      alert("Something went wrong D! Try Again.");
    }

    setLoading(false);
  };

  return (
    <Container
      fluid
      className="p-0 m-0 flex flex-col text-black w-full min-h-screen bg-[#eeeeee]  relative"
    >
      {/* Header */}
      <section className="p-4 text-[3xl] w-full h-fit bg-white font-bold">
        <h2>Add New Casino</h2>
      </section>

      {/* Form Section */}
      <section className="flex-1 p-4 justify-center items-center">
        {/* Form */}
        <form
          className="bg-white h-full w-full flex flex-col p-3"
          onSubmit={handleSubmit}
        >
          {/* Informations */}
          <SectionWrapper header="Informations">
            {/* Category */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="category"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Select Category(s)
              </label>
              <ReactSelect
                id="category"
                name="category"
                value={formData.category}
                options={[{ value: "Online Casino", label: "Online Casino" }]}
                onChange={handleSelectChange}
                className="p-2 w-full text-[0.8rem] col-span-5"
              />
            </div>
            {/* Content Name */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="contentName"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Content Name
              </label>
              <input
                type="text"
                id="contentName"
                name="contentName"
                onChange={handleInputChange}
                value={formData.contentName}
                required
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* Ref URL */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="refURL"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Ref URL
              </label>
              <input
                type="text"
                id="refURL"
                name="refURL"
                onChange={handleInputChange}
                value={formData.refURL}
                required
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* T&C Apply Link */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="taxLink"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                T&C Apply Link
              </label>
              <input
                type="text"
                id="taxLink"
                name="taxLink"
                value={formData.taxLink}
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* Welcome Bonus Text */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="welcomeBonusText"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Welcome Bonus Text
              </label>
              <input
                type="text"
                id="welcomeBonusText"
                name="welcomeBonusText"
                onChange={handleInputChange}
                value={formData.welcomeBonusText}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* Free Bonus Text */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="freeBonusText"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Free Bonus Text
              </label>
              <input
                type="text"
                id="freeBonusText"
                name="freeBonusText"
                onChange={handleInputChange}
                value={formData.freeBonusText}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* Bonus Short Description */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="bonusShortDesc"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Bonus Short Description
              </label>
              <input
                type="text"
                id="bonusShortDesc"
                name="bonusShortDesc"
                onChange={handleInputChange}
                value={formData.bonusShortDesc}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* Bonus Long Description */}
            <div className="grid grid-cols-1 grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="bonusLongDesc"
                className="block text-[0.8rem] text-gray-400 w-full items-start lg:justify-end md:px-3 flex"
              >
                Bonus Long Description
              </label>
              <div className="col-span-5">
                <TextEditor
                  name="bonusLongDesc"
                  value={formData.bonusLongDesc}
                  onChange={handleEditorChange}
                  height={400}
                  language="en"
                />
              </div>
            </div>
            {/*  Description */}
            <div className="grid grid-cols-1 grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="description"
                className="block text-[0.8rem] text-gray-400 w-full items-start lg:justify-end md:px-3 flex"
              >
                Casino Description
              </label>
              <div className="col-span-5">
                <TextEditor
                  name="description"
                  value={formData.description}
                  onChange={handleEditorChange}
                  height={400}
                  language="en"
                />
              </div>
            </div>
            {/* Profile Picture */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="picture"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Profile Picture
              </label>
              <input
                type="file"
                id="picture"
                name="picture"
                accept="image/*"
                onChange={handleInputChange}
                required={formData.picture !== "" ? false : true}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {formData.picture !== "" && (
              <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
                <div />
                <div className="p-2 w-full col-span-5">
                  <img
                    alt={formData.contentName ?? "Preview"}
                    src={formData.picture}
                    className="w-64 h-64 object-cover"
                  />
                </div>
              </div>
            )}

            {/* Cover Image */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="cover"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Cover Image
              </label>
              <input
                type="file"
                id="cover"
                name="cover"
                accept="image/*"
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {formData.cover !== "" && formData.cover !== null && (
              <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
                <div />
                <div className="p-2 w-full col-span-5">
                  <img
                    alt={formData.contentName ?? "Preview"}
                    src={formData.cover}
                    className="w-full h-32 lg:h-64 object-cover"
                  />
                </div>
              </div>
            )}
            {/* Home Sequence */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="homeSequence"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Home Sequence
              </label>
              <input
                type="text"
                id="homeSequence"
                name="homeSequence"
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
                value={formData.homeSequence}
              />
            </div>
            {/* Category Rank */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="categoryRank"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Category Rank
              </label>
              <input
                type="text"
                id="categoryRank"
                name="categoryRank"
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
                value={formData.categoryRank}
              />
            </div>
            {/* Status */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="status"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Status
              </label>
              <select
                id="status"
                name="status"
                onChange={handleInputChange}
                required
                value={formData.status}
                className="p-2 w-full text-[0.8rem] col-span-5"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </SectionWrapper>

          {/* Extra Information */}
          <SectionWrapper header="Extra Information">
            {/* Website */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="website"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Website
              </label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* Email */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="email"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* Phone */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="phone"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* Operating Since */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="operatingSince"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                E-Operating Since
              </label>
              <input
                type="date"
                id="operatingSince"
                name="operatingSince"
                value={formData.operatingSince}
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* Owners */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="owners"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Owners
              </label>
              <ReactSelect
                id="owners"
                name="owners"
                value={formData.owners}
                options={CASINO_OWNERS}
                isMulti
                onChange={handleSelectChange}
                className="p-2 w-full text-[0.8rem] col-span-5"
              />
            </div>
            {/* Testing Agency */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="testingAgency"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Testing Agency
              </label>
              <ReactSelect
                id="testingAgency"
                name="testingAgency"
                value={formData.testingAgency}
                options={CASINO_TESTING_AGENCIES}
                isMulti
                onChange={handleSelectChange}
                className="p-2 w-full text-[0.8rem] col-span-5"
              />
            </div>
            {/* Currencies */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="currencies"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Currencies
              </label>
              <ReactSelect
                id="currencies"
                name="currencies"
                value={formData.currencies}
                options={CURRENCIES}
                isMulti
                onChange={handleSelectChange}
                className="p-2 w-full text-[0.8rem] col-span-5"
              />
            </div>
            {/* Deposit Methods */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="depositMethods"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Deposit Methods
              </label>
              <ReactSelect
                id="depositMethods"
                name="depositMethods"
                value={formData.depositMethods}
                options={DEPOSIT_METHODS}
                isMulti
                onChange={handleSelectChange}
                className="p-2 w-full text-[0.8rem] col-span-5"
              />
            </div>
            {/* Pulling Methods */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="pullingMethods"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Pulling Methods
              </label>
              <ReactSelect
                id="pullingMethods"
                name="pullingMethods"
                value={formData.pullingMethods}
                options={PULLING_METHODS}
                isMulti
                onChange={handleSelectChange}
                className="p-2 w-full text-[0.8rem] col-span-5"
              />
            </div>
            {/* Customer Support */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="customerSupport"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Customer Support
              </label>
              <ReactSelect
                id="customerSupport"
                name="customerSupport"
                value={formData.customerSupport}
                options={CUSTOMER_SUPPORT}
                isMulti
                onChange={handleSelectChange}
                className="p-2 w-full text-[0.8rem] col-span-5"
              />
            </div>
            {/* Languages Covered */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="languages"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Languages Covered
              </label>
              <ReactSelect
                id="languages"
                name="languages"
                value={formData.languages}
                options={LANGUAGES}
                isMulti
                onChange={handleSelectChange}
                className="p-2 w-full text-[0.8rem] col-span-5"
              />
            </div>
            {/* Mobile Application */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="mobileApp"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Mobile Application
              </label>
              <ReactSelect
                id="mobileApp"
                name="mobileApp"
                value={formData.mobileApp}
                options={[
                  { value: "No", label: "No" },
                  { value: "Yes", label: "Yes" },
                ]}
                onChange={handleSelectChange}
                className="p-2 w-full text-[0.8rem] col-span-5"
              />
            </div>
            {/* Bonus Types */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="bonusTypes"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Bonus Types
              </label>
              <ReactSelect
                id="bonusTypes"
                name="bonusTypes"
                value={formData.bonusTypes}
                options={CASINO_BONUS_TYPES}
                isMulti
                onChange={handleSelectChange}
                className="p-2 w-full text-[0.8rem] col-span-5"
              />
            </div>
          </SectionWrapper>

          {/* Restricted Countries */}
          <SectionWrapper header="Restricted Countries">
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="allowedCountries"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Select Allowed Countries
              </label>
              <ReactSelect
                id="allowedCountries"
                name="allowedCountries"
                value={formData.allowedCountries}
                options={COUNTRIES}
                isMulti
                onChange={handleSelectChange}
                className="p-2 w-full text-[0.8rem] col-span-5"
              />
            </div>
          </SectionWrapper>

          {/* SEO */}
          <SectionWrapper header="SEO">
            {/* SEO Title */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="seoTitle"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                SEO Title
              </label>
              <input
                type="text"
                id="seoTitle"
                name="seoTitle"
                value={formData.seoTitle}
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* SEO Keywords */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="seoKeywords"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                SEO Keyword
              </label>
              <input
                type="text"
                value={formData.seoKeywords}
                id="seoKeywords"
                name="seoKeywords"
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* SEO Descriptiom */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="seoDesc"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                SEO Description
              </label>
              <textarea
                id="seoDesc"
                value={formData.seoDesc}
                name="seoDesc"
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
          </SectionWrapper>

          <div className="flex justify-end items-center">
            <button
              type="submit"
              className="bg-green-600 text-white p-2 px-4 rounded-md mx-2 flex items-center"
            >
              {state && state.id ? (
                <>
                  <FaEdit className="mr-2" /> Update
                </>
              ) : (
                <>
                  <FaSave className="mr-2" /> Save
                </>
              )}
            </button>
            <button
              type="button"
              className="bg-gray-600 text-white p-2 px-4 rounded-md flex items-center"
              onClick={() => navigate("/vips/casinos-list")}
            >
              <FaBackward className="mr-2" /> Back
            </button>
          </div>
        </form>
      </section>
      {loading && (
        <div className="absolute inset-0 bg-gray-300/30 flex items-center justify-center backdrop-blur-sm">
          <SPINNER />
        </div>
      )}
    </Container>
  );
};

export default AddNewCasino;
